import { useRef, useEffect, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { RoundedBox, MeshDistortMaterial, Octahedron } from '@react-three/drei';
import * as THREE from 'three';
import { TILE_SIZE, FLOOR_HEIGHT, ENEMY_COLORS, ENEMY_SHAPE_RATIOS } from './constants';
import { Face } from './shared/Face';
import { FireParticles } from './shared/Particles3D';
import type { EnemyState } from '@/types/enemy';

interface Enemy3DWebGLProps {
  enemy: EnemyState;
  totalCols: number;
  totalRows: number;
  isFrozen: boolean;
}

type EnemyKey = keyof typeof ENEMY_COLORS;

function getEnemyColorKey(type: string): EnemyKey {
  if (type.includes('beetle')) return 'beetle';
  if (type.includes('drone')) return 'drone';
  if (type.includes('mite')) return 'mite';
  if (type.includes('hound')) return 'hound';
  if (type.includes('mirror')) return 'mirror';
  return 'beetle';
}

function getEnemyExpression(colorKey: EnemyKey): 'smile' | 'sad' | 'shock' | 'surprise' {
  switch (colorKey) {
    case 'beetle': return 'sad';
    case 'drone': return 'shock';
    case 'mite': return 'surprise';
    case 'hound': return 'sad';
    case 'mirror': return 'shock';
    default: return 'sad';
  }
}

function getEnemyEyeColor(colorKey: EnemyKey): string {
  switch (colorKey) {
    case 'beetle': return '#ff0000';
    case 'drone': return '#00ff00';
    case 'hound': return '#ffff00';
    case 'mirror': return '#3498db';
    default: return '#000000';
  }
}

// Beetle legs sub-component
function BeetleLegs({ bodyW, bodyH }: { bodyW: number; bodyH: number }) {
  const legsRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (legsRef.current) {
      legsRef.current.children.forEach((leg, i) => {
        leg.rotation.x = Math.sin(state.clock.elapsedTime * 15 + i) * 0.3;
      });
    }
  });

  const legPositions: [number, number, number][] = [
    [-bodyW * 0.45, -bodyH * 0.45, bodyW * 0.25],
    [0, -bodyH * 0.45, bodyW * 0.25],
    [bodyW * 0.45, -bodyH * 0.45, bodyW * 0.25],
    [-bodyW * 0.45, -bodyH * 0.45, -bodyW * 0.25],
    [0, -bodyH * 0.45, -bodyW * 0.25],
    [bodyW * 0.45, -bodyH * 0.45, -bodyW * 0.25],
  ];

  return (
    <group ref={legsRef}>
      {legPositions.map((pos, i) => (
        <mesh key={i} position={pos} rotation={[0, 0, pos[0] < 0 ? Math.PI / 4 : pos[0] > 0 ? -Math.PI / 4 : 0]} castShadow>
          <capsuleGeometry args={[bodyW * 0.06, bodyH * 0.3, 4, 8]} />
          <meshStandardMaterial color="#3e2723" />
        </mesh>
      ))}
    </group>
  );
}

// Drone rotating ring
function DroneRing({ bodyH }: { bodyH: number }) {
  const ringRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (ringRef.current) {
      ringRef.current.rotation.z = state.clock.elapsedTime * 2;
      ringRef.current.scale.setScalar(1 + Math.sin(state.clock.elapsedTime * 5) * 0.1);
    }
  });

  return (
    <>
      <mesh ref={ringRef} position={[0, -bodyH * 0.4, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[bodyH * 0.4, 0.025, 16, 32]} />
        <meshStandardMaterial color="#00ff00" emissive="#00ff00" emissiveIntensity={2} />
      </mesh>
      <pointLight color="#00ff00" intensity={1.5} distance={2} position={[0, -bodyH * 0.4, 0]} />
    </>
  );
}

// Hound tongue
function HoundTongue({ bodyD }: { bodyD: number }) {
  const tongueRef = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    if (tongueRef.current) {
      tongueRef.current.scale.y = 1 + Math.sin(state.clock.elapsedTime * 20) * 0.5;
    }
  });

  return (
    <mesh ref={tongueRef} position={[0, -0.05, bodyD / 2 + 0.05]} rotation={[Math.PI / 2, 0, 0]}>
      <capsuleGeometry args={[0.03, 0.08, 4, 8]} />
      <meshStandardMaterial color="#ff2222" />
    </mesh>
  );
}

// Mirror orbiting shards
function MirrorShards({ size }: { size: number }) {
  const shardsRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (shardsRef.current) {
      shardsRef.current.rotation.y = -t * 1.5;
      shardsRef.current.children.forEach((s) => (s.rotation.x = t * 3));
    }
  });

  return (
    <group ref={shardsRef}>
      {[0, 1, 2].map((i) => (
        <mesh key={i} position={[
          Math.cos(i * (Math.PI * 2) / 3) * size * 1.8,
          0,
          Math.sin(i * (Math.PI * 2) / 3) * size * 1.8,
        ]}>
          <tetrahedronGeometry args={[size * 0.3]} />
          <meshBasicMaterial color="#00aaff" />
        </mesh>
      ))}
    </group>
  );
}

export default function Enemy3DWebGL({ enemy, totalCols, totalRows, isFrozen }: Enemy3DWebGLProps) {
  const groupRef = useRef<THREE.Group>(null);
  const bodyRef = useRef<THREE.Mesh>(null);
  const miteBodyRef = useRef<THREE.Group>(null);
  const targetPos = useRef(new THREE.Vector3());
  const currentPos = useRef(new THREE.Vector3());
  const initialized = useRef(false);
  const isSliding = useRef(false);
  const prevEnemyPos = useRef<[number, number]>(enemy.position);

  const colorKey = getEnemyColorKey(enemy.type);
  const colors = ENEMY_COLORS[colorKey];
  const shape = ENEMY_SHAPE_RATIOS[colorKey];

  const baseSize = 0.48;
  const bodyW = baseSize * shape.w;
  const bodyH = baseSize * shape.h;
  const bodyD = baseSize * shape.d;
  const borderRadius = Math.min(bodyW, bodyH, bodyD) * shape.radius;

  const baseY = FLOOR_HEIGHT + bodyH / 2 + 0.02;

  const gridToWorld = (pos: [number, number]) => {
    const x = (pos[0] - (totalCols - 1) / 2) * TILE_SIZE;
    const z = (pos[1] - (totalRows - 1) / 2) * TILE_SIZE;
    return new THREE.Vector3(x, baseY, z);
  };

  useEffect(() => {
    const worldPos = gridToWorld(enemy.position);
    targetPos.current.copy(worldPos);
    if (!initialized.current) {
      currentPos.current.copy(worldPos);
      initialized.current = true;
    }

    // Detect when enemy actually moved grid positions
    const prev = prevEnemyPos.current;
    if (prev[0] !== enemy.position[0] || prev[1] !== enemy.position[1]) {
      isSliding.current = true;
      // Clear sliding state after lerp settles
      setTimeout(() => { isSliding.current = false; }, 500);
    }
    prevEnemyPos.current = enemy.position;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enemy.position, totalCols, totalRows]);

  const expression = getEnemyExpression(colorKey);
  const eyeColor = getEnemyEyeColor(colorKey);
  const faceScale = Math.min(bodyW, bodyH, bodyD);

  useFrame((state, delta) => {
    if (!groupRef.current) return;
    const t = state.clock.getElapsedTime();

    // Smooth position lerp
    const prevPos = currentPos.current.clone();
    currentPos.current.lerp(targetPos.current, Math.min(delta * 10, 1));
    groupRef.current.position.copy(currentPos.current);

    // Detect visual movement for squash-stretch
    const moveSpeed = prevPos.distanceTo(currentPos.current) / Math.max(delta, 0.001);
    const isVisuallyMoving = moveSpeed > 0.5;

    // Type-specific idle animations (more dynamic to show life between turns)
    switch (colorKey) {
      case 'beetle':
        // Crawling bob with slight forward lean
        groupRef.current.position.y = currentPos.current.y + Math.sin(t * 3) * 0.04;
        if (bodyRef.current) {
          bodyRef.current.rotation.z = Math.sin(t * 2) * 0.06;
          bodyRef.current.rotation.x = Math.sin(t * 2.5) * 0.04;
        }
        break;
      case 'drone':
        // Hovering bob (more pronounced floating feel)
        groupRef.current.position.y = currentPos.current.y + Math.sin(t * 3) * 0.08 + 0.04;
        if (bodyRef.current) {
          bodyRef.current.rotation.z = Math.sin(t * 2) * 0.05;
          // Slight tilt in movement direction
          bodyRef.current.rotation.x = Math.sin(t * 1.5) * 0.06;
        }
        break;
      case 'mite':
        // Bouncy ball: rapid bounce with spin
        groupRef.current.position.y = currentPos.current.y + Math.abs(Math.sin(t * 6)) * 0.08;
        if (miteBodyRef.current) {
          miteBodyRef.current.rotation.x = t * 5;
          miteBodyRef.current.rotation.z = t * 3;
        }
        break;
      case 'hound':
        // Prowling: low bob with restless head movement
        groupRef.current.position.y = currentPos.current.y + Math.sin(t * 2.5) * 0.03;
        if (bodyRef.current) {
          bodyRef.current.rotation.x = Math.sin(t * 1.5) * 0.1;
          // Occasional quick head turn
          bodyRef.current.rotation.z = Math.sin(t * 3) * 0.04;
        }
        break;
      case 'mirror':
        // Mystic float with slow oscillation
        groupRef.current.position.y = currentPos.current.y + Math.sin(t * 1.5) * 0.06 + 0.03;
        if (bodyRef.current) {
          bodyRef.current.rotation.y = t * 1.2;
          bodyRef.current.rotation.x = Math.sin(t * 0.8) * 0.3;
        }
        break;
    }

    // Movement squash-stretch when sliding
    if (bodyRef.current && isVisuallyMoving && colorKey !== 'mirror' && colorKey !== 'mite') {
      const stretch = 1 + Math.min(moveSpeed * 0.02, 0.15);
      const squash = 1 / Math.sqrt(stretch);
      bodyRef.current.scale.set(squash, squash, stretch);
    }

    // Face direction with smooth interpolation
    const targetRot =
      enemy.direction === 'left' ? -Math.PI / 2 :
      enemy.direction === 'right' ? Math.PI / 2 :
      enemy.direction === 'up' ? Math.PI : 0;

    if (colorKey !== 'mirror' && colorKey !== 'mite') {
      groupRef.current.rotation.y += (targetRot - groupRef.current.rotation.y) * Math.min(delta * 8, 1);
    }

    // Stunned wobble
    if (enemy.isStunned) {
      groupRef.current.rotation.z = Math.sin(t * 12) * 0.3;
    } else if (colorKey !== 'mirror') {
      groupRef.current.rotation.z *= 0.9;
    }

    // Breathing scale (when not moving)
    if (bodyRef.current && !enemy.isStunned && !isVisuallyMoving && colorKey !== 'mirror' && colorKey !== 'mite') {
      const breathe = 1 + Math.sin(t * 2.5) * 0.04;
      bodyRef.current.scale.set(1, breathe, 1);
    }
  });

  const frozenBody = isFrozen ? '#88CCFF' : colors.body;

  return (
    <group ref={groupRef}>
      {/* Type-specific body */}
      {colorKey === 'mirror' ? (
        // Mirror: Octahedron with metallic finish
        <>
          <Octahedron ref={bodyRef} args={[Math.max(bodyW, bodyH) * 0.6, 0]} castShadow>
            <meshPhysicalMaterial
              color={isFrozen ? '#88CCFF' : '#223344'}
              metalness={1}
              roughness={0.05}
            />
          </Octahedron>
          {/* Glowing core spheres */}
          <mesh position={[0, 0, bodyD * 0.8]}>
            <sphereGeometry args={[bodyW * 0.15, 16, 16]} />
            <meshStandardMaterial color="#3498db" emissive="#3498db" emissiveIntensity={2} />
          </mesh>
          <mesh position={[0, 0, -bodyD * 0.8]}>
            <sphereGeometry args={[bodyW * 0.15, 16, 16]} />
            <meshStandardMaterial color="#3498db" emissive="#3498db" emissiveIntensity={2} />
          </mesh>
          <MirrorShards size={Math.max(bodyW, bodyH) * 0.5} />
          <pointLight color="#00aaff" intensity={1.5} distance={2.5} />
        </>
      ) : colorKey === 'mite' ? (
        // Mite: Sphere with rapid spin
        <group ref={miteBodyRef}>
          <mesh castShadow>
            <sphereGeometry args={[Math.max(bodyW, bodyH) * 0.55, 32, 32]} />
            <meshStandardMaterial
              color={frozenBody}
              roughness={0.9}
              metalness={isFrozen ? 0.4 : 0.1}
              emissive={isFrozen ? '#4488CC' : colors.body}
              emissiveIntensity={isFrozen ? 0.2 : 0.06}
            />
          </mesh>
          <Face expression={expression} eyeColor={eyeColor} scale={faceScale} />
        </group>
      ) : colorKey === 'hound' ? (
        // Hound: Wide body with distort material, ears, tongue, fire
        <>
          <RoundedBox
            ref={bodyRef}
            args={[bodyW, bodyH, bodyD]}
            radius={borderRadius}
            smoothness={3}
            castShadow
          >
            {isFrozen ? (
              <meshStandardMaterial
                color="#88CCFF"
                metalness={0.4}
                roughness={0.1}
                emissive="#4488CC"
                emissiveIntensity={0.2}
              />
            ) : (
              <MeshDistortMaterial
                color={colors.body}
                roughness={0.6}
                distort={0.2}
                speed={2}
              />
            )}
          </RoundedBox>
          <Face expression={expression} eyeColor={eyeColor} glow scale={faceScale} />
          {/* Ears */}
          <mesh position={[-bodyW * 0.35, bodyH * 0.55, bodyD * 0.2]} rotation={[0, 0, Math.PI / 8]}>
            <coneGeometry args={[bodyW * 0.1, bodyH * 0.35, 4]} />
            <meshStandardMaterial color="#922b21" />
          </mesh>
          <mesh position={[bodyW * 0.35, bodyH * 0.55, bodyD * 0.2]} rotation={[0, 0, -Math.PI / 8]}>
            <coneGeometry args={[bodyW * 0.1, bodyH * 0.35, 4]} />
            <meshStandardMaterial color="#922b21" />
          </mesh>
          <HoundTongue bodyD={bodyD} />
          {!isFrozen && <FireParticles count={8} bounds={[bodyW, bodyH * 0.8, bodyD] as [number, number, number]} />}
          <pointLight color="#ff5500" intensity={1.5} distance={2} />
        </>
      ) : (
        // Beetle and Drone: standard RoundedBox
        <>
          <RoundedBox
            ref={bodyRef}
            args={[bodyW, bodyH, bodyD]}
            radius={borderRadius}
            smoothness={3}
            castShadow
          >
            <meshStandardMaterial
              color={frozenBody}
              metalness={isFrozen ? 0.4 : 0.1}
              roughness={isFrozen ? 0.1 : colorKey === 'beetle' ? 0.8 : 0.4}
              emissive={isFrozen ? '#4488CC' : colors.body}
              emissiveIntensity={isFrozen ? 0.2 : 0.06}
            />
          </RoundedBox>
          <Face expression={expression} eyeColor={eyeColor} glow={colorKey === 'drone'} scale={faceScale} />
          {colorKey === 'beetle' && <BeetleLegs bodyW={bodyW} bodyH={bodyH} />}
          {colorKey === 'drone' && <DroneRing bodyH={bodyH} />}
        </>
      )}

      {/* Shadow beneath */}
      <mesh position={[0, -bodyH / 2 + 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[Math.max(bodyW, bodyD) * 0.45, 16]} />
        <meshBasicMaterial color="black" transparent opacity={0.2} />
      </mesh>

      {/* Frozen overlay */}
      {isFrozen && (
        <mesh>
          <sphereGeometry args={[Math.max(bodyW, bodyH, bodyD) * 0.65, 12, 12]} />
          <meshPhysicalMaterial
            color="#88DDFF"
            transparent
            opacity={0.25}
            clearcoat={1}
            clearcoatRoughness={0.02}
          />
        </mesh>
      )}
    </group>
  );
}
